import { useEffect, useState, useMemo, useCallback } from 'react';
import { CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { getHazardTractsDebug } from '../services/api';

/**
 * Debug overlay for the wildfire/flood heatmaps.
 *
 * Renders a small dot per NRI census tract centroid in view, color-coded by
 * raw score. Click any dot to see the actual values in a popup. Used to
 * sanity-check why a region renders dim or hot in the main heatmap — the
 * heatmap aggregates + thresholds + grid-spreads, this overlay is
 * one-dot-per-tract with no filtering at all.
 *
 * Props:
 *   layer:   'wildfire' | 'flood'  (which raw column to color by)
 *   metric:  'risk' | 'frequency'
 *   enabled: bool
 */
function HazardTractsDebugLayer({ layer = 'wildfire', metric = 'risk', enabled = false }) {
  const map = useMap();
  const [tracts, setTracts] = useState([]);

  const load = useCallback(async () => {
    if (!enabled) {
      setTracts([]);
      return;
    }
    const b = map.getBounds();
    const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
    try {
      const data = await getHazardTractsDebug(bbox, layer, metric, 2000);
      setTracts(data?.tracts || []);
    } catch (err) {
      // Don't surface debug-overlay errors to the user — silent
      // failure is fine; this is a developer tool.
      // eslint-disable-next-line no-console
      console.warn('Hazard tracts debug load failed:', err);
      setTracts([]);
    }
  }, [enabled, map, layer, metric]);

  useMapEvents({ moveend: load, zoomend: load });
  useEffect(() => { load(); }, [load]);

  // Color scale matching the heatmap gradient at ~25/50/75/95th percentile.
  // Wildfire risk ranges 0-100; AFREQ ranges 0-1ish for wildfire, 0-30 for flood.
  // Scaling per-layer so the eye can pick up gradients in either dataset.
  const intensityCutoffs = useMemo(() => {
    if (metric === 'frequency') {
      return layer === 'wildfire'
        ? [3.4e-5, 5e-4, 1.5e-3, 4.5e-3]   // p50, ~p75, ~p90, p95 of wildfire_afreq
        : [2.07, 3.5, 5.0, 7.25];          // p50..p95 of rflood_afreq
    }
    return [25, 50, 70, 85];                // FEMA NRI score buckets
  }, [layer, metric]);

  const colorFor = (score) => {
    if (score == null) return '#888';
    const [c1, c2, c3, c4] = intensityCutoffs;
    if (score < c1) return '#999';
    if (score < c2) return '#fed976';
    if (score < c3) return '#fd8d3c';
    if (score < c4) return '#e31a1c';
    return '#800026';
  };

  if (!enabled || tracts.length === 0) return null;

  return (
    <>
      {tracts.map((t) => (
        <CircleMarker
          key={t.tract_id}
          center={[t.lat, t.lon]}
          pathOptions={{
            color: colorFor(t.score),
            fillColor: colorFor(t.score),
            fillOpacity: 0.75,
            weight: 1,
            radius: 5,
          }}
        >
          <Popup maxWidth={280} minWidth={220}>
            <div style={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, fontSize: '0.85rem' }}>
                Tract {t.tract_id}
              </div>
              <div style={{ color: '#666', marginBottom: 6 }}>
                {t.county}, {t.state}
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                  <tr><td style={{ paddingRight: 8, color: '#666' }}>Wildfire (loss)</td><td style={{ textAlign: 'right' }}>{fmt(t.wildfire_score)}</td></tr>
                  <tr><td style={{ paddingRight: 8, color: '#666' }}>Wildfire (AFREQ)</td><td style={{ textAlign: 'right' }}>{fmt(t.wildfire_afreq, 5)}</td></tr>
                  <tr><td style={{ paddingRight: 8, color: '#666' }}>Flood (loss)</td><td style={{ textAlign: 'right' }}>{fmt(t.flood_score)}</td></tr>
                  <tr><td style={{ paddingRight: 8, color: '#666' }}>Flood (AFREQ)</td><td style={{ textAlign: 'right' }}>{fmt(t.rflood_afreq, 3)}</td></tr>
                </tbody>
              </table>
              <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#888' }}>
                Active heatmap: {layer} / {metric} = <strong>{fmt(t.score, 5)}</strong>
              </div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

function fmt(v, decimals = 2) {
  if (v == null) return '—';
  if (decimals === 0) return Math.round(v).toString();
  return Number(v).toFixed(decimals);
}

export default HazardTractsDebugLayer;
